import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, CheckCircle2, XCircle } from 'lucide-react';
import { axiosInstance } from '@/App';
import { toast } from 'sonner';

const Register = ({ onLogin }) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = searchParams.get('session_id');

  const [verifying, setVerifying] = useState(!!sessionId); // Only verify if session_id exists
  const [paymentValid, setPaymentValid] = useState(!sessionId); // Free registration is always "valid"
  const [paymentData, setPaymentData] = useState(null);
  const [registering, setRegistering] = useState(false);

  const [formData, setFormData] = useState({
    username: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: ''
  });

  useEffect(() => {
    if (sessionId) {
      // Paid registration - verify payment
      verifyPayment();
    } else {
      // Free registration - no verification needed
      setVerifying(false);
      setPaymentValid(true);
    }
  }, [sessionId]);

  const verifyPayment = async () => {
    try {
      setVerifying(true);
      
      // Poll payment status
      let attempts = 0;
      const maxAttempts = 5;
      
      while (attempts < maxAttempts) {
        const response = await axiosInstance.get(`/payments/verify-session/${sessionId}`);
        
        if (response.data.valid && response.data.payment_status === 'paid') {
          setPaymentValid(true);
          setPaymentData(response.data);
          if (response.data.email) {
            setFormData(prev => ({ ...prev, email: response.data.email }));
          }
          break;
        }
        
        if (response.data.payment_status === 'expired' || response.data.payment_status === 'failed') {
          setPaymentValid(false);
          toast.error('Zahlung fehlgeschlagen oder abgelaufen');
          break;
        }
        
        // Wait 2 seconds before next attempt
        await new Promise(resolve => setTimeout(resolve, 2000));
        attempts++;
      }
      
      if (attempts >= maxAttempts && !paymentValid) {
        toast.error('Zahlungsverifizierung fehlgeschlagen');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler bei der Zahlungsverifizierung');
      setTimeout(() => navigate('/pricing'), 3000);
    } finally {
      setVerifying(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.username || !formData.email || !formData.password || !formData.phone) {
      toast.error('Bitte füllen Sie alle Pflichtfelder aus');
      return;
    }

    // Validate phone number format (basic check for mobile)
    const phoneRegex = /^[\+]?[(]?[0-9]{1,4}[)]?[-\s\.]?[0-9]{1,4}[-\s\.]?[0-9]{1,9}$/;
    if (!phoneRegex.test(formData.phone.replace(/\s/g, ''))) {
      toast.error('Bitte geben Sie eine gültige Mobilnummer ein');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwörter stimmen nicht überein');
      return;
    }

    if (formData.password.length < 6) {
      toast.error('Passwort muss mindestens 6 Zeichen lang sein');
      return;
    }

    setRegistering(true);

    try {
      const response = await axiosInstance.post('/auth/register', {
        username: formData.username,
        email: formData.email,
        phone: formData.phone,
        password: formData.password,
        payment_session_id: sessionId
      });

      // KRITISCH: Token SOFORT in localStorage speichern BEVOR irgendwas anderes passiert!
      localStorage.setItem('token', response.data.access_token);
      localStorage.setItem('username', response.data.username);
      localStorage.setItem('showThankYou', 'true');
      
      toast.success('Registrierung erfolgreich! Willkommen!');
      
      // Dann onLogin aufrufen um React-State zu aktualisieren
      onLogin(response.data.access_token, response.data.username, true);
      
      // Zur Thank-You Seite navigieren
      navigate('/thank-you');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler bei der Registrierung');
    } finally {
      setRegistering(false);
    }
  };

  if (verifying) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md shadow-2xl">
          <CardContent className="pt-12 pb-12 text-center">
            <Loader2 className="w-16 h-16 animate-spin text-purple-600 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-800 mb-2">
              Zahlung wird überprüft...
            </h2>
            <p className="text-gray-600">
              Bitte warten Sie einen Moment
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!paymentValid) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md shadow-2xl border-4 border-red-300">
          <CardContent className="pt-12 pb-12 text-center">
            <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-800 mb-2">
              Zahlung nicht gültig
            </h2>
            <p className="text-gray-600 mb-6">
              Ihre Zahlung konnte nicht verifiziert werden.
            </p>
            <Button
              onClick={() => navigate('/pricing')}
              className="bg-purple-600 hover:bg-purple-700"
            >
              Zurück zur Preisauswahl
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md shadow-2xl">
        <CardHeader className="bg-gradient-to-r from-purple-600 to-pink-600 text-white">
          <div className="flex items-center justify-center mb-4">
            <CheckCircle2 className="w-12 h-12" />
          </div>
          <CardTitle className="text-2xl text-center">
            {sessionId ? 'Zahlung erfolgreich!' : 'Kostenfrei testen!'}
          </CardTitle>
          <p className="text-center text-purple-100 mt-2">
            {sessionId 
              ? (paymentData?.subscription_type === 'lifetime' 
                  ? '🎉 Lifetime-Zugang gesichert!' 
                  : '✨ Monatliches Abo aktiviert!')
              : '🚀 Test-Zugang aktiviert!'}
          </p>
        </CardHeader>
        <CardContent className="pt-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="username">Benutzername</Label>
              <Input
                id="username"
                type="text"
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                placeholder="IhrBenutzername"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">E-Mail</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="ihre@email.de"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone">Mobilnummer *</Label>
              <Input
                id="phone"
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="+49 171 1234567"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Passwort</Label>
              <Input
                id="password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder="Mindestens 6 Zeichen"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Passwort bestätigen</Label>
              <Input
                id="confirmPassword"
                type="password"
                value={formData.confirmPassword}
                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                placeholder="Passwort wiederholen"
                required
              />
            </div>

            <Button
              type="submit"
              disabled={registering}
              className="w-full h-12 text-lg font-semibold bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
            >
              {registering ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Registrierung läuft...
                </>
              ) : (
                'Account erstellen'
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default Register;
